import { useEffect, useState } from 'react';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Box, Button, ButtonBase, Chip, CircularProgress, Stack, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { ModalIOF } from '@old/features/ui/modal/modal';
import { useService } from '@old/hooks/use-service';
import { assignSilobagTagsService } from '@old/services/domain/base/silobag/tag/silobag-tag';
import { silobagTagListService } from '@old/services/domain/base/silobag/tag/silobag-tag-list';
import i18n from '../../../libs/language';
import { useShallow } from 'zustand/react/shallow';
import { useGlobalStore } from '@old/global-store/use-store';

type SilobagTagModalProps = {
  open: boolean;
  setOpen: (value: boolean) => void;
  siloId: string;
  siloName: string;
  onTagsAssigned: () => void;
};

export function SilobagTagModal({ open, setOpen, siloId, siloName, onTagsAssigned }: SilobagTagModalProps) {
  const { preferedLanguageId } = useGlobalStore(useShallow((s) => s));

  const [selected, setSelected] = useState<number[]>([]);

  const { summary: tagsData, isLoading: isLoadingTags } = useService(silobagTagListService, {
    fetchOnMount: [{ query: siloId }],
  });

  const { trigger: assignTags, isLoading: isAssigning } = useService(assignSilobagTagsService, {
    onSuccess: () => {
      setOpen(false);
      onTagsAssigned();
    },
  });

  useEffect(() => {
    if (!tagsData) return;
    const preSelected =
      tagsData.response
        .find((scope) => scope.scope === 'organization')
        ?.tags.filter((entry) => entry.selected)
        .map((entry) => entry.tag.id) ?? [];
    setSelected(preSelected);
  }, [tagsData]);

  const orgTags = tagsData?.response.find((scope) => scope.scope === 'organization')?.tags ?? [];

  const handleToggle = (id: number) => {
    if (selected.includes(id)) {
      setSelected((prev) => prev.filter((s) => s !== id));
      return;
    }

    setSelected((prev) => [...prev, id]);
  };

  const handleConfirm = () => {
    assignTags({ query: siloId, body: { tags: selected.map((id) => ({ id })) } });
  };

  const handleClose = () => {
    setSelected([]);
    setOpen(false);
  };

  return (
    <ModalIOF open={open} ModalIsOpen={handleClose} width="520px">
      <Stack gap={2.5}>
        <Stack gap={0.5}>
          <Typography variant="h6" fontWeight={700}>
            {i18n.t('app.silobag_tags.assign')}
          </Typography>
          <Typography variant="body2" color="text.secondary" fontSize={13}>
            <strong>{siloName}</strong> — {i18n.t('app.silobag_tags.description')}
          </Typography>
        </Stack>

        <Box
          sx={{
            maxHeight: 260,
            overflowY: 'auto',
            pr: 0.5,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { borderRadius: 4, bgcolor: '#f1f5f9' },
            '&::-webkit-scrollbar-thumb': { borderRadius: 4, bgcolor: '#cbd5e1' },
          }}
        >
          {isLoadingTags ? (
            <Stack alignItems="center" py={3}>
              <CircularProgress size={24} sx={{ color: '#6BAA00' }} />
            </Stack>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, max-content)',
                justifyContent: 'space-evenly',
                justifyItems: 'start',
                rowGap: 1,
              }}
            >
              {orgTags.map(({ tag }) => {
                const isChecked = selected.includes(tag.id);
                const translation =
                  tag.translations.find((p) => p.language == preferedLanguageId)?.value || tag.label;
                const translationTooltip = tag.translations.find(
                  (p) => p.language == preferedLanguageId
                )?.tooltip;
                const surfaceBorder = isChecked ? '#6BAA00' : '#D1D5DB';
                const surfaceBackground = isChecked ? '#e1eecc' : '#F8FAFC';

                return (
                  <Box key={tag.id}>
                    <ButtonBase
                      aria-label={translation}
                      aria-pressed={isChecked}
                      onClick={() => handleToggle(tag.id)}
                      sx={{
                        maxWidth: '100%',
                        borderRadius: 2,
                        justifyContent: 'flex-start',
                        transition: 'background-color 150ms ease',
                        '&:hover .tag-toggle-segment': {
                          bgcolor: isChecked ? '#d4e8b4' : '#EEF0F2',
                        },
                        '&.Mui-focusVisible': {
                          outline: '2px solid #6BAA00',
                          outlineOffset: 2,
                        },
                      }}
                    >
                      <Box
                        component="span"
                        className="tag-toggle-segment"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          width: 32,
                          height: 32,
                          color: isChecked ? '#5a9400' : '#9CA3AF',
                          bgcolor: surfaceBackground,
                          border: '1px solid',
                          borderColor: surfaceBorder,
                          borderRightWidth: 0,
                          borderRadius: '8px 0 0 8px',
                          transition:
                            'color 150ms ease, border-color 150ms ease, background-color 150ms ease',
                        }}
                      >
                        <AddRoundedIcon fontSize="small" />
                      </Box>
                      <Tooltip title={translationTooltip || ''}>
                        <Box
                          component="span"
                          className="tag-toggle-segment"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            minWidth: 0,
                            minHeight: 32,
                            p: '3px',
                            bgcolor: surfaceBackground,
                            border: '1px solid',
                            borderColor: surfaceBorder,
                            borderLeftWidth: 0,
                            borderRadius: '0 8px 8px 0',
                            transition: 'border-color 150ms ease, background-color 150ms ease',
                          }}
                        >
                          <Chip
                            icon={
                              tag.image_url ? (
                                <img
                                  src={tag.image_url}
                                  alt=""
                                  style={{ width: 14, height: 14, marginLeft: 4, objectFit: 'contain' }}
                                />
                              ) : undefined
                            }
                            label={translation}
                            size="small"
                            sx={{
                              maxWidth: '100%',
                              backgroundColor: tag.bg_color,
                              color: tag.text_color,
                              fontWeight: 600,
                              fontSize: '12px',
                              height: 24,
                              borderRadius: '6px',
                              pointerEvents: 'none',
                              '& .MuiChip-label': {
                                px: '10px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              },
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </ButtonBase>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        <Stack direction="row" alignItems="flex-start" gap={0.75}>
          <HelpOutlineIcon sx={{ fontSize: 18, color: '#6BAA00', mt: '1px', flexShrink: 0 }} />
          <Typography variant="caption" color="#6BAA00" lineHeight={1.5}>
            <strong>{i18n.t('app.silobag_tags.question')}</strong>
            <br />
            {i18n.t('app.silobag_tags.contact')}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" gap={1.5} pt={0.5}>
          <Button variant="text" onClick={handleClose} sx={{ color: '#374151', fontWeight: 600 }}>
            {i18n.t('app.close')}
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={isAssigning}
            sx={{
              bgcolor: '#6BAA00',
              color: '#fff',
              fontWeight: 600,
              borderRadius: '20px',
              px: 3,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#5a9300', boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#6BAA0066', color: '#fff' },
            }}
          >
            {isAssigning ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : i18n.t('app.confirm')}
          </Button>
        </Stack>
      </Stack>
    </ModalIOF>
  );
}
